/**
 * <mango-sow> — SOW completion (Equipment Processing).
 * =====================================================================
 * The provider documents the batch and submits the SOW, which advances the work
 * order to FMV Approval. Real contract operations fired via the data-action
 * registry (client.ts):
 *
 *   • sow                → submit { weight_total, weight_ewaste,
 *                          completed_deliverables } (stamps sow_submitted_at;
 *                          advances → FMV Approval). Body from buildSowBody().
 *   • inventory          → upload the asset-inventory CSV (multipart), then POLL
 *                          the signed task id until the import settles.
 *   • fmv                → upload the per-item FMV assessment CSV (multipart,
 *                          writes fmv_1; allowed in Equipment Processing).
 *   • deliverable-upload → upload one SOW deliverable file (cod /
 *                          ewaste_disposal_cert) → marks it complete.
 *   • save-draft         → persist the un-submitted form state to sow_draft.
 *
 * On load the form is RESTORED from job.sow_draft (weights + scope notes) when
 * present.
 *
 * COUNTDOWN: a DD:HH:MM clock is computed from job.sla_due_at (set in Equipment
 * Processing) and ticked once a minute via setInterval; the interval is cleared
 * in disconnectedCallback (and whenever the deadline passes / is absent).
 *
 * COLLAPSIBLE SECTIONS: each [data-section-toggle] header toggles its sibling
 * .section-body and rotates the chevron — wired here (CSP-safe, keyboard-operable
 * via the delegated click/keydown), NO inline onclick in the view.
 *
 * ROLE-GATING IS COSMETIC (server enforces): the LIVE OVERAGES panel is shown
 * only when job.overages is present (omitted from Staff responses) — gating on
 * FIELD PRESENCE, never a client-side role flag. Uploads + submit work for both
 * roles (Q6).
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field; no bind references it.
 */
// uploadInventory / submitFmv / uploadDeliverable / submitSow / saveDraft are
// fired via the data-action registry (client.ts); only the GET poll
// (pollInventory) is called directly here.
import {
  getWorkOrder,
  pollInventory,
  uploadWorkOrderPhoto,
  deleteWorkOrderPhoto,
  type WorkOrder,
  type SowSubmit,
  type SaveDraftBody,
  type WorkOrderPhoto,
} from "../api/client";
import { ApiError } from "../api/http";
import { applyBindings, wireActions } from "../bind";
import { loadView, date, money, setText } from "./shared";

/**
 * Pull the server's real error reason off a failed request. The API returns a
 * 400/500 with a JSON `{ detail }` body (e.g. a rejected file type, or the
 * 0-row inventory import) — surface THAT instead of a generic "retry" string.
 * Falls back to the caller's generic message when no detail is present.
 */
function errorDetail(error: unknown, fallback: string): string {
  const body = error instanceof ApiError ? (error.body as { detail?: unknown } | null) : null;
  const detail = body?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}

/**
 * A successful inventory import may carry a non-blocking `warning` inside its
 * `result` JSON — e.g. device types the importer didn't recognise and filed as
 * "Misc" (J16: provider categories are normalised, never rejected). Returns the
 * warning text if present, else null. Loose-cast at the boundary (the warning
 * rides inside the opaque `result` string, not the typed contract).
 */
function inventoryWarning(res: unknown): string | null {
  const raw = (res as { result?: unknown } | undefined)?.result;
  if (typeof raw !== "string") return null;
  try {
    const warning = (JSON.parse(raw) as { warning?: unknown }).warning;
    return typeof warning === "string" && warning.trim() ? warning : null;
  } catch {
    return null;
  }
}

const VIEW_URL = new URL("../../views/sow.html", import.meta.url).href;

/** Max poll attempts for the async CSV import (each ~1s apart). */
const POLL_LIMIT = 15;

/** This screen captures one photo set — "facility processing" (kind=facility),
 *  distinct from the onsite Before/Packed-truck sets. Cap: 10. The backend
 *  accepts `facility` (migration 0112) and the contract type carries it. */
const FACILITY_KIND: WorkOrderPhoto["kind"] = "facility";
const FACILITY_PHOTO_CAP = 10;

/** The deliverable types this screen can upload a file for (deliverable-upload). */
const DELIVERABLE_UPLOADS = {
  cod: { fileSel: "[data-cod-file]", statusSel: "[data-cod-status]" },
  ewaste_disposal_cert: { fileSel: "[data-ewaste-file]", statusSel: "[data-ewaste-status]" },
} as const;
type DeliverableUploadType = keyof typeof DELIVERABLE_UPLOADS;

export class MangoSow extends HTMLElement {
  static observedAttributes = ["data-short-name"];
  private disposeActions: (() => void) | null = null;
  private job: WorkOrder | null = null;
  private countdownTimer: number | null = null;
  private slaDueMs: number | null = null;

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    this.disposeActions = wireActions(this, {
      getBody: (ev) => {
        if (ev.action === "sow") return this.buildSowBody();
        if (ev.action === "save-draft") return this.buildDraftBody();
        if (ev.action === "inventory") {
          const form = this.buildFileForm("[data-inventory-file]");
          if (!form) {
            this.setUploadStatus("[data-inventory-status]", "Choose an inventory CSV first.", "err");
            return false;
          }
          return form;
        }
        if (ev.action === "fmv") {
          const form = this.buildFileForm("[data-fmv-file]");
          if (!form) {
            this.setUploadStatus("[data-fmv-status]", "Choose an FMV assessment CSV first.", "err");
            return false;
          }
          return form;
        }
        if (ev.action === "deliverable-upload") {
          return this.buildDeliverableForm(ev.element);
        }
        return undefined;
      },
      onSuccess: (ev, result) => {
        if (ev.action === "sow") {
          // SOW submission advances the job to FMV Approval — go to that screen,
          // where the provider/Admin reviews the FMV settlement and finalizes.
          this.setStatus("SOW submitted — advancing to FMV Approval…");
          window.location.hash = `#/finalize/${encodeURIComponent(ev.id)}`;
        } else if (ev.action === "save-draft") {
          this.setDraftStatus("Draft saved · just now");
          // Reflect the persisted draft on the live job for completeness derivation.
          if (result) this.job = result as WorkOrder;
          this.renderProgress();
        } else if (ev.action === "inventory") {
          void this.afterInventoryUpload(ev.id, result);
        } else if (ev.action === "fmv") {
          this.setUploadStatus("[data-fmv-status]", "FMV assessment uploaded.", "ok");
          if (result) this.job = result as WorkOrder;
          void this.load();
        } else if (ev.action === "deliverable-upload") {
          // deliverable-upload returns the updated work order (deliverable marked
          // complete) — refresh so the checks + progress reflect it.
          if (result) this.job = result as WorkOrder;
          void this.load();
        }
      },
      onError: (ev, error) => {
        console.error(`[mango-sow] ${ev.action} failed for ${ev.id}`, error);
        // Surface the server's real reason (400/500 `detail`) — e.g. a rejected
        // file type or a 0-row import — not a generic "retry" string.
        if (ev.action === "inventory") this.setUploadStatus("[data-inventory-status]", errorDetail(error, "Inventory upload failed. Please retry."), "err");
        else if (ev.action === "fmv") this.setUploadStatus("[data-fmv-status]", errorDetail(error, "FMV upload failed. Please retry."), "err");
        else if (ev.action === "deliverable-upload") this.setDeliverableError(ev.element, error);
        else if (ev.action === "save-draft") this.setDraftStatus("Could not save the draft. Please retry.");
        else this.setStatus("Could not submit the SOW. Please retry.");
      },
    });

    this.wireSections();
    this.wireRemaining();
    this.wireFiles();
    this.wirePhotos();
    this.wireScopeNotes();
    await this.load();
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
    this.stopCountdown();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) void this.load();
  }

  private get shortName(): string {
    return this.getAttribute("data-short-name") ?? "";
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  private setDraftStatus(message: string): void {
    setText(this, "[data-draft-status]", message);
  }

  private setUploadStatus(selector: string, message: string, kind: "ok" | "err" | ""): void {
    const el = this.querySelector<HTMLElement>(selector);
    if (!el) return;
    el.textContent = message;
    el.className = `upload-status${kind ? " " + kind : ""}`;
  }

  // ── Collapsible sections (CSP-safe) ─────────────────────────────────────────
  /** Toggle each section header's sibling body + rotate its chevron on click /
   *  Enter / Space. The delegated handler is attached to the host element. */
  private wireSections(): void {
    const onToggle = (target: HTMLElement): void => {
      const hdr = target.closest<HTMLElement>("[data-section-toggle]");
      if (!hdr) return;
      const section = hdr.closest<HTMLElement>(".form-section");
      const body = section?.querySelector<HTMLElement>(".section-body");
      const chevron = hdr.querySelector<HTMLElement>(".section-chevron");
      if (!body) return;
      // .section-body has `display:flex` in CSS (which beats the [hidden] UA rule),
      // so we collapse by toggling the inline display — the CSP bans inline onclick,
      // not inline style; mirrors shared.toggle().
      const open = body.style.display === "none";
      body.style.display = open ? "" : "none";
      hdr.setAttribute("aria-expanded", String(open));
      chevron?.classList.toggle("collapsed", !open);
    };
    this.addEventListener("click", (e) => onToggle(e.target as HTMLElement));
    this.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const hdr = (e.target as HTMLElement).closest<HTMLElement>("[data-section-toggle]");
      if (!hdr) return;
      e.preventDefault();
      onToggle(e.target as HTMLElement);
    });
  }

  // ── "What's remaining" disclosure (CSP-safe) ────────────────────────────────
  /** Rotate the chevron when the native <details> opens/closes. The list contents
   *  are filled in renderRemaining (from the same derived completeness). */
  private wireRemaining(): void {
    const details = this.querySelector<HTMLDetailsElement>("[data-remaining-details]");
    const chevron = this.querySelector<HTMLElement>("[data-remaining-chevron]");
    details?.addEventListener("toggle", () => {
      chevron?.classList.toggle("collapsed", !details.open);
    });
  }

  // ── File-input → button-enable wiring ───────────────────────────────────────
  private wireFiles(): void {
    const pairs: ReadonlyArray<[string, string, string]> = [
      ["[data-inventory-file]", "[data-inventory-btn]", "[data-inventory-status]"],
      ["[data-fmv-file]", "[data-fmv-btn]", "[data-fmv-status]"],
      ["[data-cod-file]", "[data-cod-btn]", "[data-cod-status]"],
      ["[data-ewaste-file]", "[data-ewaste-btn]", "[data-ewaste-status]"],
    ];
    for (const [fileSel, btnSel, statusSel] of pairs) {
      const input = this.querySelector<HTMLInputElement>(fileSel);
      const btn = this.querySelector<HTMLButtonElement>(btnSel);
      input?.addEventListener("change", () => {
        if (btn) btn.disabled = !(input.files && input.files.length > 0);
        this.setUploadStatus(statusSel, "", "");
      });
    }
  }

  // ── Scope-expansion notes ────────────────────────────────────────────────────
  /** Live-react to scope-notes typing: re-run renderProgress so the Optional bar /
   *  section check reflect the note immediately, and nudge the user that the note
   *  is saved with "Save draft" (it has no dedicated backend field). On blur we
   *  surface a reminder in the draft bar so it's clear the note isn't auto-saved. */
  private wireScopeNotes(): void {
    const notes = this.querySelector<HTMLTextAreaElement>("[data-scope-notes]");
    if (!notes) return;
    notes.addEventListener("input", () => this.renderProgress());
    notes.addEventListener("blur", () => {
      if (notes.value.trim()) this.setDraftStatus("Unsaved scope notes — click “Save draft” to keep them.");
    });
  }

  // ── Facility processing photos (real uploads) ────────────────────────────────
  /** Wire the facility section's add tile (opens the file input), the file input
   *  change (upload each picked File), and a delegated ✕ click (delete). Mirrors
   *  mango-onsite's photo wiring EXACTLY, scoped to the single facility kind.
   *  CSP-safe (addEventListener; the inputs/buttons are NOT data-action/bind). */
  private wirePhotos(): void {
    const add = this.querySelector<HTMLElement>('[data-photo-add="facility"]');
    add?.addEventListener("click", () => this.photoInput()?.click());

    const input = this.photoInput();
    input?.addEventListener("change", () => {
      const files = input.files ? Array.from(input.files) : [];
      input.value = ""; // allow re-picking the same file later
      void this.uploadPhotos(files);
    });

    // Delegated ✕ on a thumbnail → delete that photo.
    this.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-photo-remove]");
      if (btn) void this.removePhoto(btn);
    });
  }

  private photoInput(): HTMLInputElement | null {
    return this.querySelector<HTMLInputElement>('[data-photo-input][data-photo-kind="facility"]');
  }

  private photoGrid(): HTMLElement | null {
    return this.querySelector<HTMLElement>('[data-photo-grid="facility"]');
  }

  private addTile(): HTMLElement | null {
    return this.querySelector<HTMLElement>('[data-photo-add="facility"]');
  }

  /** Current uploaded thumbnail count (excludes the add tile). */
  private photoCount(): number {
    return this.photoGrid()?.querySelectorAll("[data-photo-id]").length ?? 0;
  }

  private setPhotoStatus(message: string, isError = false): void {
    const el = this.querySelector<HTMLElement>('[data-photo-status="facility"]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("err", isError);
  }

  /** Enable/disable the add tile once the section hits its cap. */
  private refreshAddTile(): void {
    const add = this.addTile();
    if (add) (add as HTMLButtonElement).disabled = this.photoCount() >= FACILITY_PHOTO_CAP;
  }

  /** Render a saved photo as a thumbnail tile inserted before the add tile. */
  private appendThumb(photo: WorkOrderPhoto): void {
    const grid = this.photoGrid();
    if (!grid) return;
    const tile = document.createElement("div");
    tile.className = "photo-slot filled photo-thumb";
    tile.dataset.photoId = String(photo.id);

    const img = document.createElement("img");
    img.src = photo.url;
    img.alt = "Facility processing photo";
    tile.appendChild(img);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "photo-remove";
    remove.dataset.photoRemove = "";
    remove.setAttribute("aria-label", "Remove photo");
    const x = document.createElement("i");
    x.className = "ti ti-x";
    remove.appendChild(x);
    tile.appendChild(remove);

    grid.insertBefore(tile, this.addTile());
  }

  /** Upload each picked file (respecting the cap), appending thumbs. A transient
   *  "uploading" placeholder shows immediately, replaced by the real thumbnail. */
  private async uploadPhotos(files: File[]): Promise<void> {
    if (!files.length) return;
    let room = FACILITY_PHOTO_CAP - this.photoCount();
    if (room <= 0) {
      this.setPhotoStatus(`Maximum ${FACILITY_PHOTO_CAP} photos reached.`, true);
      return;
    }
    const queued = files.slice(0, room);
    const skipped = files.length - queued.length;

    const add = this.addTile();
    let done = 0;
    for (const file of queued) {
      this.setPhotoStatus(`Uploading ${done + 1} of ${queued.length}…`);
      // A transient "uploading" placeholder tile (no image — blob: previews are
      // blocked by the CSP, so we render the real thumbnail from the server URL).
      const ph = document.createElement("div");
      ph.className = "photo-slot photo-thumb uploading";
      const spinner = document.createElement("i");
      spinner.className = "ti ti-refresh";
      ph.appendChild(spinner);
      this.photoGrid()?.insertBefore(ph, add);
      try {
        const photo = await uploadWorkOrderPhoto(this.shortName, FACILITY_KIND, file);
        ph.remove();
        this.appendThumb(photo);
        done++;
      } catch (error) {
        console.error("[mango-sow] facility photo upload failed", error);
        ph.remove();
        this.setPhotoStatus(errorDetail(error, "Upload failed. Please retry."), true);
        this.refreshAddTile();
        this.renderProgress();
        return;
      }
    }
    this.refreshAddTile();
    this.setPhotoStatus(
      skipped > 0 ? `Uploaded ${done}. ${skipped} skipped — max ${FACILITY_PHOTO_CAP}.` : "",
      skipped > 0,
    );
    this.renderProgress();
  }

  /** Delete the photo for a ✕ button, then remove its tile. */
  private async removePhoto(button: HTMLElement): Promise<void> {
    const tile = button.closest<HTMLElement>("[data-photo-id]");
    const id = tile?.dataset.photoId;
    if (!tile || !id) return;
    button.setAttribute("aria-busy", "true");
    try {
      await deleteWorkOrderPhoto(this.shortName, Number(id));
      tile.remove();
      this.refreshAddTile();
      this.setPhotoStatus("");
      this.renderProgress();
    } catch (error) {
      console.error("[mango-sow] facility photo delete failed", error);
      button.removeAttribute("aria-busy");
      this.setPhotoStatus("Could not remove the photo. Please retry.", true);
    }
  }

  /** Paint the saved FACILITY photos from the loaded WO (the contract `photos`
   *  list carries all kinds — onsite Before/Truck + facility — so we filter to
   *  facility here). Re-render is idempotent. */
  private renderPhotos(job: WorkOrder): void {
    const rec = job as unknown as Record<string, unknown>;
    const photos = Array.isArray(rec.photos) ? (rec.photos as WorkOrderPhoto[]) : [];
    this.photoGrid()?.querySelectorAll("[data-photo-id]").forEach((t) => t.remove());
    for (const photo of photos) {
      if (photo && (photo.kind as string) === "facility") this.appendThumb(photo);
    }
    this.refreshAddTile();
  }

  /** Build a multipart form { file } from a file input, or null. */
  private buildFileForm(fileSel: string): FormData | null {
    const input = this.querySelector<HTMLInputElement>(fileSel);
    const file = input?.files?.[0];
    if (!file) return null;
    const form = new FormData();
    form.append("file", file, file.name);
    return form;
  }

  /** Map a deliverable-upload button to its type, then build { deliverable_type, file }. */
  private deliverableTypeFor(button: HTMLElement): DeliverableUploadType | null {
    if (button.closest("[data-cod-btn]")) return "cod";
    if (button.closest("[data-ewaste-btn]")) return "ewaste_disposal_cert";
    return null;
  }

  private buildDeliverableForm(button: HTMLElement): FormData | false {
    const type = this.deliverableTypeFor(button);
    if (!type) return false;
    const { fileSel, statusSel } = DELIVERABLE_UPLOADS[type];
    const input = this.querySelector<HTMLInputElement>(fileSel);
    const file = input?.files?.[0];
    if (!file) {
      this.setUploadStatus(statusSel, "Choose a file first.", "err");
      return false;
    }
    const form = new FormData();
    form.append("deliverable_type", type);
    form.append("file", file, file.name);
    return form;
  }

  private setDeliverableError(button: HTMLElement, error?: unknown): void {
    const type = this.deliverableTypeFor(button);
    if (type) this.setUploadStatus(DELIVERABLE_UPLOADS[type].statusSel, errorDetail(error, "Upload failed. Please retry."), "err");
  }

  /** Build the typed SOW body: weights + the deliverable types we treat as done. */
  private buildSowBody(): SowSubmit {
    const num = (sel: string): string | null => {
      const v = this.querySelector<HTMLInputElement>(sel)?.value.trim() ?? "";
      return v === "" ? null : v;
    };
    type DeliverableType = NonNullable<SowSubmit["completed_deliverables"]>[number];
    const completed = new Set<DeliverableType>();

    const total = num("[data-weight-total]");
    const ewaste = num("[data-weight-ewaste]");
    // Weight entry marks the two weight deliverables complete in the submission.
    if (total != null) completed.add("total_weight");
    if (ewaste != null) completed.add("ewaste_weight");
    // Carry forward any deliverable the server already marked complete (uploads).
    for (const d of this.job?.deliverables ?? []) {
      if (d.status === "complete") completed.add(d.deliverable_type as DeliverableType);
    }

    const body: SowSubmit = { completed_deliverables: [...completed] };
    if (total != null) body.weight_total = total;
    if (ewaste != null) body.weight_ewaste = ewaste;
    return body;
  }

  /** Build the save-draft body: current weights + scope-expansion notes. */
  private buildDraftBody(): SaveDraftBody {
    const val = (sel: string): string => this.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel)?.value.trim() ?? "";
    return {
      draft: {
        weight_total: val("[data-weight-total]"),
        weight_ewaste: val("[data-weight-ewaste]"),
        scope_notes: val("[data-scope-notes]"),
      },
    };
  }

  /** Restore the form from job.sow_draft (weights + scope notes) when present. */
  private restoreDraft(job: WorkOrder): void {
    const draft = job.sow_draft;
    if (!draft || typeof draft !== "object") return;
    const d = draft as Record<string, unknown>;
    const set = (sel: string, value: unknown): void => {
      if (value == null || value === "") return;
      const el = this.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel);
      if (el && el.value === "") el.value = String(value);
    };
    set("[data-weight-total]", d.weight_total);
    set("[data-weight-ewaste]", d.weight_ewaste);
    set("[data-scope-notes]", d.scope_notes);
  }

  /**
   * Populate weight inputs from the persisted job.weight_total / job.weight_ewaste
   * when the input is still empty after restoreDraft (i.e. no active draft covers it).
   * Precedence: active draft > persisted submitted value > empty.
   * Only applies when the deliverable ledger already marks the weight as complete —
   * so a resumed SOW whose weights were submitted but whose draft was cleared shows
   * the real submitted values instead of blank placeholders.
   */
  private restorePersistedWeights(job: WorkOrder): void {
    const setIfEmpty = (sel: string, value: string | null | undefined): void => {
      if (value == null || value === "") return;
      const el = this.querySelector<HTMLInputElement>(sel);
      if (el && el.value === "") el.value = value;
    };
    setIfEmpty("[data-weight-total]", job.weight_total);
    setIfEmpty("[data-weight-ewaste]", job.weight_ewaste);
  }

  // ── SLA countdown ────────────────────────────────────────────────────────────
  private startCountdown(slaDueAt: string | null | undefined): void {
    this.stopCountdown();
    const panel = this.querySelector<HTMLElement>("[data-countdown]");
    if (!slaDueAt) {
      if (panel) panel.style.display = "none";
      return;
    }
    const due = new Date(slaDueAt).getTime();
    if (Number.isNaN(due)) {
      if (panel) panel.style.display = "none";
      return;
    }
    this.slaDueMs = due;
    if (panel) panel.style.display = "";
    this.tickCountdown();
    // Tick once a minute (the display is DD:HH:MM — no need for per-second updates).
    this.countdownTimer = window.setInterval(() => this.tickCountdown(), 60_000);
  }

  private tickCountdown(): void {
    if (this.slaDueMs == null) return;
    let mins = Math.floor((this.slaDueMs - Date.now()) / 60_000);
    if (mins < 0) mins = 0; // never show a negative countdown; clamp at zero
    const days = Math.floor(mins / 1440);
    const hours = Math.floor((mins % 1440) / 60);
    const minutes = mins % 60;
    const pad = (n: number): string => String(n).padStart(2, "0");
    setText(this, "[data-countdown-days]", pad(days));
    setText(this, "[data-countdown-hrs]", pad(hours));
    setText(this, "[data-countdown-min]", pad(minutes));
    if (mins === 0) this.stopCountdown(); // deadline reached — stop ticking
  }

  private stopCountdown(): void {
    if (this.countdownTimer != null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private async afterInventoryUpload(shortName: string, result: unknown): Promise<void> {
    const task = (result as { task?: string } | undefined)?.task;
    if (!task) {
      this.setUploadStatus("[data-inventory-status]", "Inventory uploaded.", "ok");
      void this.load();
      return;
    }
    this.setUploadStatus("[data-inventory-status]", "Importing inventory…", "");
    for (let i = 0; i < POLL_LIMIT; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const res = await pollInventory(shortName, task);
        if (res) {
          const warning = inventoryWarning(res);
          this.setUploadStatus(
            "[data-inventory-status]",
            warning ? `Inventory imported. ${warning}` : "Inventory imported.",
            "ok",
          );
          void this.load();
          return;
        }
      } catch (error) {
        console.error("[mango-sow] inventory poll failed", error);
        // A 0-row / malformed import fails server-side (500 + `detail`) — show the
        // real reason so the user knows it didn't import, not a vague "refresh".
        this.setUploadStatus("[data-inventory-status]", errorDetail(error, "Could not confirm the import. Refresh to check."), "err");
        return;
      }
    }
    this.setUploadStatus("[data-inventory-status]", "Import is taking longer than expected. Refresh to check.", "");
  }

  private async load(): Promise<void> {
    const id = this.shortName;
    if (!id) {
      this.setStatus("No work order specified.");
      return;
    }
    this.setStatus("Loading work order…");
    try {
      this.job = await getWorkOrder(id);
    } catch (error) {
      console.error("[mango-sow] failed to load", error);
      this.setStatus("Could not load this work order.");
      return;
    }
    this.render();
  }

  private render(): void {
    const job = this.job;
    if (!job) return;
    applyBindings(this, { job });
    this.querySelectorAll<HTMLElement>('[data-bind="job.dropoff_datetime"]').forEach(
      (el) => (el.textContent = date(job.dropoff_datetime)),
    );
    this.renderEquipmentDescription(job);
    this.restoreDraft(job);
    this.restorePersistedWeights(job);
    this.startCountdown(job.sla_due_at);
    this.renderOverages(job);
    this.renderPhotos(job);
    this.renderProgress();
    this.setStatus("");
  }

  /**
   * Read-only equipment description — entered once at pickup (onsite form) and
   * locked here for chain-of-custody integrity. Rendered into [data-equip-desc]
   * as plain text. If the field is absent or empty, a placeholder note is shown
   * instead so the card never appears blank.
   */
  private renderEquipmentDescription(job: WorkOrder): void {
    const el = this.querySelector<HTMLElement>("[data-equip-desc]");
    if (!el) return;
    const raw = (job as unknown as Record<string, unknown>).onsite_equipment_description;
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text) {
      el.textContent = text;
      el.style.color = "";
    } else {
      el.textContent = "No description recorded at pickup.";
      el.style.color = "var(--text-3)";
    }
  }

  /**
   * LIVE OVERAGES — Admin-only. Rendered only when job.overages is present (a
   * Staff response omits it); gating on FIELD PRESENCE, never a role flag. Values
   * are dollar strings from the contract, shown verbatim with a `+$` prefix.
   */
  private renderOverages(job: WorkOrder): void {
    const panel = this.querySelector<HTMLElement>("[data-overages-panel]");
    const o = (job as Record<string, unknown>).overages as
      | { wipe_overage: string; ewaste_overage: string; flat_rate: string; total_credits: string }
      | undefined;
    if (!panel) return;
    if (!o) {
      panel.style.display = "none";
      return;
    }
    panel.style.display = "";
    setText(this, "[data-overage-wipe]", `+$${money(o.wipe_overage)}`);
    setText(this, "[data-overage-ewaste]", `+$${money(o.ewaste_overage)}`);
    setText(this, "[data-overage-flat]", `+$${money(o.flat_rate)}`);
    setText(this, "[data-overage-total]", `+$${money(o.total_credits)}`);
  }

  /**
   * COMPLETION PROGRESS (best-effort, clearly derived):
   *   Required (4): weights (total_weight + ewaste_weight both done),
   *                 asset_inventory, cod, fmv (fmv_submitted_at set).
   *   Optional (3): ewaste_disposal_cert, facility photos (≥1 uploaded),
   *                 scope expansion (notes present in the form/draft).
   * Deliverable completeness comes from job.deliverables statuses; the current
   * form values are folded in for the weights + scope notes.
   */
  private renderProgress(): void {
    const job = this.job;
    if (!job) return;
    const done = new Set<string>();
    for (const d of job.deliverables) if (d.status === "complete") done.add(d.deliverable_type);

    const formVal = (sel: string): boolean => (this.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel)?.value.trim() ?? "") !== "";
    const weightsDone =
      (done.has("total_weight") || formVal("[data-weight-total]")) &&
      (done.has("ewaste_weight") || formVal("[data-weight-ewaste]"));
    const inventoryDone = done.has("asset_inventory");
    // Show the "already uploaded" affordance when the asset_inventory deliverable
    // is complete so a resumed SOW doesn't show a bare "No file selected" under a
    // green check. The element is hidden by default in the markup.
    const invDoneEl = this.querySelector<HTMLElement>("[data-inventory-done]");
    if (invDoneEl) invDoneEl.style.display = inventoryDone ? "" : "none";
    const codDone = done.has("cod");
    const fmvDone = job.fmv_submitted_at != null;

    const required = [weightsDone, inventoryDone, codDone, fmvDone];
    const reqCount = required.filter(Boolean).length;

    const ewasteCertDone = done.has("ewaste_disposal_cert");
    const photosDone = this.photoCount() > 0; // real facility photos uploaded
    const scopeDone = formVal("[data-scope-notes]");
    const optional = [ewasteCertDone, photosDone, scopeDone];
    const optCount = optional.filter(Boolean).length;

    this.setProgress("[data-progress-req-count]", "[data-progress-req-bar]", reqCount, required.length);
    this.setProgress("[data-progress-opt-count]", "[data-progress-opt-bar]", optCount, optional.length);
    this.markSectionChecks({ weightsDone, inventoryDone, codDone, fmvDone, ewasteCertDone, photosDone, scopeDone });
    this.renderRemaining({ weightsDone, inventoryDone, codDone, fmvDone, ewasteCertDone, scopeDone });

    // Mirror into the pinned sticky bar (X / N format) + gate the submit on
    // Required 4/4 (matches the prototype's "N of 4 required" label).
    const pct = (n: number, t: number): string => `${t ? Math.round((n / t) * 100) : 0}%`;
    setText(this, "[data-sticky-req-count]", `${reqCount} / ${required.length}`);
    setText(this, "[data-sticky-opt-count]", `${optCount} / ${optional.length}`);
    const reqBar = this.querySelector<HTMLElement>("[data-sticky-req-bar]");
    if (reqBar) reqBar.style.width = pct(reqCount, required.length);
    const optBar = this.querySelector<HTMLElement>("[data-sticky-opt-bar]");
    if (optBar) optBar.style.width = pct(optCount, optional.length);
    const allReq = reqCount >= required.length;
    const submitBtn = this.querySelector<HTMLButtonElement>("[data-sow-btn]");
    if (submitBtn) submitBtn.disabled = !allReq;
    setText(this, "[data-sow-label]", allReq ? "Submit SOW completion" : `${reqCount} of ${required.length} required`);
  }

  private setProgress(countSel: string, barSel: string, n: number, total: number): void {
    setText(this, countSel, `${n} of ${total}`);
    const bar = this.querySelector<HTMLElement>(barSel);
    if (bar) bar.style.width = `${total ? Math.round((n / total) * 100) : 0}%`;
  }

  /** Toggle the green per-section check chips from the derived completeness. */
  private markSectionChecks(state: {
    weightsDone: boolean;
    inventoryDone: boolean;
    codDone: boolean;
    fmvDone: boolean;
    ewasteCertDone: boolean;
    photosDone: boolean;
    scopeDone: boolean;
  }): void {
    const checks = this.querySelectorAll<HTMLElement>("[data-section-check]");
    // The sections render in a fixed order: weights, inventory, cod, fmv,
    // ewaste-cert, facility-photos, scope.
    const order = [
      state.weightsDone,
      state.inventoryDone,
      state.codDone,
      state.fmvDone,
      state.ewasteCertDone,
      state.photosDone, // facility photos — real uploads now counted
      state.scopeDone,
    ];
    checks.forEach((el, i) => el.classList.toggle("visible", Boolean(order[i])));
  }

  /**
   * "What's remaining" disclosure: list each Required / Optional deliverable by
   * its DISPLAY NAME, marking the done ones checked (green ✓) and the still-missing
   * ones with a hollow circle. Derived from the SAME completeness booleans
   * renderProgress already computes (no new source of truth).
   * (Facility photos is the Phase-2 placeholder — never counted, so omitted.)
   */
  private renderRemaining(state: {
    weightsDone: boolean;
    inventoryDone: boolean;
    codDone: boolean;
    fmvDone: boolean;
    ewasteCertDone: boolean;
    scopeDone: boolean;
  }): void {
    const required: ReadonlyArray<[string, boolean]> = [
      ["Weight documentation", state.weightsDone],
      ["Asset Inventory", state.inventoryDone],
      ["Certificate of Data Destruction", state.codDone],
      ["FMV assessment", state.fmvDone],
    ];
    const optional: ReadonlyArray<[string, boolean]> = [
      ["E-waste downstream certification", state.ewasteCertDone],
      ["Scope expansion notes", state.scopeDone],
    ];
    this.fillRemaining("[data-remaining-req]", required);
    this.fillRemaining("[data-remaining-opt]", optional);
  }

  /** Render one remaining-list: done items checked, missing items hollow. */
  private fillRemaining(selector: string, items: ReadonlyArray<[string, boolean]>): void {
    const list = this.querySelector<HTMLElement>(selector);
    if (!list) return;
    list.replaceChildren();
    for (const [label, done] of items) {
      const li = document.createElement("li");
      li.style.cssText = "display:flex;align-items:center;gap:6px;font-size:12px";
      li.style.color = done ? "var(--text-3)" : "var(--text)";
      const icon = document.createElement("i");
      icon.className = done ? "ti ti-circle-check" : "ti ti-alert-circle";
      icon.style.cssText = `font-size:14px;flex-shrink:0;color:${done ? "var(--green)" : "var(--amber)"}`;
      const text = document.createElement("span");
      text.textContent = done ? label : `${label} — still needed`;
      if (done) text.style.textDecoration = "line-through";
      li.append(icon, text);
      list.appendChild(li);
    }
  }
}

export function registerMangoSow(): void {
  if (!customElements.get("mango-sow")) {
    customElements.define("mango-sow", MangoSow);
  }
}
